/* ═══════════════════════════════════════════════════════════════
   Shared formatters, small display primitives, and style objects for
   the Project Center report widgets (T5a). Ported from the pre-R1
   dashboard-widgets module (shared.tsx) — `ProjectWidgetCtx` and
   `DashboardDrill` now live in widgets/context.ts (the T3+ contract),
   and `WidgetSkeleton`/`WidgetEmptyState` are replaced everywhere by
   the shared `WidgetFrame` (components/widgets/WidgetFrame.tsx), so
   none of the four are re-exported here.
   ═══════════════════════════════════════════════════════════════ */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import type { TeamMember, BillingRole } from '../../../../shared/types';
import { Icon } from '../../../components/ui/Icon';
import { rem } from '../../../lib/rem';
// axisTick / chartTooltipStyle now live in components/widgets/WChart.tsx (the
// T2 kit) — re-exported here (T3A task 9) so every report-widget import site
// keeps working unchanged while there's a single source of truth.
export { axisTick, chartTooltipStyle } from '../../../components/widgets/WChart';

/** Size tier a widget body renders — structurally identical to
 *  `WidgetSize` (components/WidgetGrid.tsx); kept as a local alias so
 *  every ported widget file can keep importing `type Size` unchanged. */
export type Size = 'compact' | 'standard' | 'expanded';

export type FetchStatus = 'loading' | 'ready' | 'error';

/** One fetch pattern every report widget shares: run a batch of promises
 *  via `Promise.allSettled` (never `Promise.all` — one slow/failed source
 *  must never blank the rest), feed the settled results to the caller so
 *  it can pick fulfilled values, and report 'error' if anything rejected
 *  so `WidgetFrame` can show "Try again" instead of a skeleton that spins
 *  forever (the bug this replaces: a bare `await` with no catch, or a
 *  `setLoading(false)` that never runs on throw). */
export function useSettledFetch(
  makeLoaders: () => Promise<any>[],
  onSettle: (results: PromiseSettledResult<any>[]) => void,
  deps: React.DependencyList
): { status: FetchStatus; reload: () => Promise<void> } {
  const [status, setStatus] = useState<FetchStatus>('loading');
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const reload = useCallback(async () => {
    setStatus('loading');
    const results = await Promise.allSettled(makeLoaders());
    if (!mountedRef.current) return;
    onSettle(results);
    setStatus(results.some((r) => r.status === 'rejected') ? 'error' : 'ready');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { status, reload };
}

// ─── Shared formatters / colors ────────────────────────────────────

export const fmtMoney = (n: number) =>
  '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
export const fmtHours = (n: number) =>
  (n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) + 'h';
export const fmtPct = (n: number) =>
  (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }) + '%';

export const COLOR_OK = 'var(--ok)';
export const COLOR_WARN = 'var(--color-warning)';
export const COLOR_DANGER = 'var(--dg)';
export const COLOR_INFO = 'var(--color-info)';
export const COLOR_NEUTRAL = 'var(--color-text-subtle)';
// Deliberate chart-series color: distinguishes "forecast" from "actual" (COLOR_INFO) across
// every burn/forecast chart in this file. Uses the --color-smart token (purple) — a series
// identity color, not a severity/status token — per DESIGN.md's data-series-palette exception.
export const COLOR_FORECAST = 'var(--color-smart)';

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.floor((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
}

export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export function shortWeek(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return `${MONTHS[dt.getUTCMonth()]} ${dt.getUTCDate()}`;
}

export function shortDate(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}

export function projectMemberRate(m: TeamMember | undefined, billingRoles: BillingRole[]): number {
  if (!m) return 0;
  const role = billingRoles.find((r) => r.id === m.billingRoleId);
  return m.hourlyRate ?? role?.defaultRate ?? 0;
}

// ─── List anatomy (Widgets R2 Decision 2) ──────────────────────────
// count chips → rows → "+ Add" row → "View all N". `countChip` and
// `AddRow` were private to the Deliverables widget; lifted here so
// Attention/Tasks/Team/Specs share one implementation (Decision 2).

/** Small rounded count pill — "3 overdue", "12 open" — tinted by `color`. */
export function countChip(color: string): React.CSSProperties {
  return {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    padding: `${rem(2)} var(--space-2)`,
    borderRadius: 'var(--radius-full)',
    background: `color-mix(in srgb, ${color} 12%, transparent)`,
    color,
  };
}

/** One-line "+ Add …" row for the bottom of a list body (WList's `footer`
 *  slot) — a plain clickable row (Fitts-floor `Row` primitive), not an
 *  inline form. Used where the action opens a drill/dialog rather than
 *  taking name/date input inline (Deliverables keeps its own
 *  `QuickAddDeliverable` inline-form variant). */
export function AddRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Row onClick={onClick} ariaLabel={label}>
      <Icon
        icon={Plus}
        size="sm"
        aria-hidden="true"
        style={{ color: 'var(--tm)', flexShrink: 0 }}
      />
      <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}>{label}</span>
    </Row>
  );
}

// ─── Reusable subcomponents ────────────────────────────────────────

export function ProgressBar({
  pct,
  color,
  height = 8,
}: {
  pct: number;
  color: string;
  height?: number;
}) {
  return (
    <div
      style={{
        height,
        background: 'var(--sa)',
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          background: color,
          transform: `scaleX(${Math.min(100, pct) / 100})`,
          transformOrigin: 'left',
          transition: 'transform var(--duration-slow) var(--ease-out)',
        }}
      />
      {pct > 100 && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '100%',
            background:
              'repeating-linear-gradient(45deg, transparent 0 6px, color-mix(in srgb, var(--dg) 25%, transparent) 6px 10px)',
          }}
        />
      )}
    </div>
  );
}

/** Accessible clickable list row — hover highlight, keyboard reachable,
 *  ≥32px effective hit area (DESIGN.md §4 Fitts floor). */
export function Row({
  onClick,
  children,
  ariaLabel,
  style,
}: {
  onClick?: () => void;
  children: React.ReactNode;
  ariaLabel?: string;
  style?: React.CSSProperties;
}) {
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={ariaLabel}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      onMouseEnter={(e) => {
        if (clickable) (e.currentTarget as HTMLDivElement).style.background = 'var(--sa)';
      }}
      onMouseLeave={(e) => {
        if (clickable) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-1) var(--space-2)',
        minHeight: rem(32),
        borderRadius: 'var(--radius-sm)',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background var(--duration-fast) var(--ease-out)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Compact / KPI tile shell ──────────────────────────────────────

export function KpiTile({
  value,
  label,
  bar,
  color,
  onClick,
  ariaLabel,
}: {
  value: string;
  label: string;
  bar?: { pct: number; color: string };
  color?: string;
  onClick?: () => void;
  ariaLabel?: string;
}) {
  const isClickable = !!onClick;
  return (
    <div
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={ariaLabel || `${value} ${label}`}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-1)',
        cursor: isClickable ? 'pointer' : 'default',
        height: '100%',
        justifyContent: 'center',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 700,
          color: color || 'var(--tx)',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>{label}</div>
      {bar && <ProgressBar pct={bar.pct} color={bar.color} />}
    </div>
  );
}

export const subText: React.CSSProperties = { fontSize: 'var(--font-size-sm)', color: 'var(--tm)' };
// Number hierarchy is deliberately quiet (Notion/Linear register): ONE hero
// number per card at most; supporting stats a step down. Color only when it
// carries a status signal.
export const bigNum: React.CSSProperties = {
  fontSize: 'var(--font-size-xl)',
  fontWeight: 700,
  color: 'var(--tx)',
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.1,
};
export const heroNum: React.CSSProperties = { ...bigNum, fontSize: 'var(--font-size-2xl)' };
// Stat rows cluster LEFT with fixed rhythm — never spread edge-to-edge to
// "use" the card width (that reads as filler, not information).
export const statRow: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  columnGap: 'var(--space-7)',
  rowGap: 'var(--space-3)',
  alignItems: 'baseline',
};

// ─── Shared local styles (moved from bottom of original monolith) ─

export const sectionLabel: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  color: 'var(--tm)',
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  marginBottom: 'var(--space-2)',
};

export const inputStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-base)',
  fontFamily: 'var(--font-mono)',
};

export const btnGhost: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'transparent',
  color: 'var(--tm)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
};

export function btnPrimary(disabled: boolean): React.CSSProperties {
  return {
    padding: 'var(--space-2) var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    background: 'var(--ac)',
    color: 'var(--color-text-inverse)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };
}

export const linkBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--ac)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
  padding: 0,
};

export function calloutStyle(accent: string): React.CSSProperties {
  return {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--tm)',
    padding: 'var(--space-2) var(--space-3)',
    background: 'var(--sa)',
    borderRadius: 'var(--radius-md)',
    borderLeft: `3px solid ${accent}`,
  };
}
